import React, { useRef, useState } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

/**
 * TTSPlayButton — knop die een stukje tekst uitspreekt via OpenAI TTS.
 *
 * Props:
 *   text      — string : de tekst om voor te lezen
 *   voice     — "nova" (default) | alloy | echo | fable | onyx | shimmer
 *   speed     — 0.5 .. 2.0 (default 1.0)
 *   size      — "xs" | "sm" (default xs)
 *   testId    — data-testid
 *
 * Tap-to-toggle: 1x klik → laden + afspelen · 2e klik → stop.
 */
export default function TTSPlayButton({
  text,
  voice = "nova",
  speed = 1.05,
  size = "xs",
  testId = "tts-play",
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const cleanup = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    if (urlRef.current) {
      try { URL.revokeObjectURL(urlRef.current); } catch {}
      urlRef.current = null;
    }
    setPlaying(false);
  };

  const stop = () => {
    cleanup();
  };

  const play = async () => {
    const clean = (text || "").trim();
    if (!clean) {
      toast.warning("Geen tekst om voor te lezen");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        "/ai/voice/speak",
        { text: clean.slice(0, 3800), voice, speed, format: "mp3" },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = cleanup;
      audio.onerror = () => { cleanup(); toast.error("Afspelen mislukt"); };
      setPlaying(true);
      await audio.play();
    } catch (err) {
      const msg = err.response?.data
        ? (typeof err.response.data === "string" ? err.response.data : formatErr(err.response.data?.detail))
        : err.message;
      toast.error(msg || "TTS fout");
      cleanup();
    }
    setBusy(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (busy) return;
    if (playing) stop();
    else play();
  };

  const sizeClasses = size === "sm" ? "h-8 w-8" : "h-7 w-7";
  const iconClass = size === "sm" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={playing ? "Stop uitspreken" : "Voorlees dit bericht"}
      aria-label={playing ? "Stop uitspreken" : "Voorlees"}
      className={`inline-flex items-center justify-center rounded-md ${sizeClasses} border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-50 transition-colors ${playing ? "text-[hsl(var(--brand-blue))] border-[hsl(var(--brand-blue))]/40" : ""} ${className}`}
      data-testid={testId}
    >
      {busy ? <Loader2 className={`${iconClass} animate-spin`} /> : playing ? <Square className={iconClass} /> : <Volume2 className={iconClass} />}
    </button>
  );
}
